// i18n — trainee-facing internationalisation for the 10 Indian languages mandated by
// the ABG / Propulse Cables & Wires LMS scope (v3). Admin surfaces remain English-only.
//
// Strategy:
//   • One canonical English dictionary (source of truth, fully populated).
//   • Hindi (hi) populated manually — covers all critical CTA strings.
//   • Other 8 languages (ta, ml, te, kn, bn, or, mr, gu) ship as stubs that fall back to
//     English via the resolver; copy can be filled in later without code changes.
//   • Language preference persists in localStorage AND on the User record (saved_locale).
//
// Usage in components:
//   import { useT } from '@/lib/i18n';
//   const t = useT();
//   <button>{t('common.start')}</button>

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// ---- Supported locales (per scope) ----
export const LOCALES = [
    { code: 'en', label: 'English',  native: 'English',   flag: '🇬🇧' },
    { code: 'hi', label: 'Hindi',    native: 'हिन्दी',     flag: '🇮🇳' },
    { code: 'ta', label: 'Tamil',    native: 'தமிழ்',      flag: '🇮🇳' },
    { code: 'ml', label: 'Malayalam',native: 'മലയാളം',     flag: '🇮🇳' },
    { code: 'te', label: 'Telugu',   native: 'తెలుగు',     flag: '🇮🇳' },
    { code: 'kn', label: 'Kannada',  native: 'ಕನ್ನಡ',       flag: '🇮🇳' },
    { code: 'bn', label: 'Bengali',  native: 'বাংলা',       flag: '🇮🇳' },
    { code: 'or', label: 'Odia',     native: 'ଓଡ଼ିଆ',       flag: '🇮🇳' },
    { code: 'mr', label: 'Marathi',  native: 'मराठी',       flag: '🇮🇳' },
    { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી',     flag: '🇮🇳' },
];

// ---- Canonical English dictionary ----
const EN = {
    common: {
        start: 'Start', continue: 'Continue', save: 'Save', cancel: 'Cancel', close: 'Close',
        next: 'Next', back: 'Back', loading: 'Loading…', error: 'Something went wrong',
        retry: 'Retry', search: 'Search', filter: 'Filter', clear: 'Clear', submit: 'Submit',
        ok: 'OK', yes: 'Yes', no: 'No', confirm: 'Confirm', delete: 'Delete', edit: 'Edit',
        login: 'Login', logout: 'Sign Out', register: 'Register', profile: 'Profile',
        select_language: 'Select Language', language: 'Language',
    },
    nav: {
        home: 'Home', courses: 'Courses', arcade: 'Arcade', leaders: 'Leaders',
        profile: 'Profile', certificates: 'Certificates', rewards: 'Rewards',
    },
    trainee: {
        welcome: 'Welcome',
        my_courses: 'My Courses',
        all_courses: 'All Courses',
        my_points: 'My Points',
        my_level: 'My Level',
        my_badges: 'My Badges',
        streak: 'Streak',
        days: 'days',
        play_arcade: 'Play Arcade',
        learn_more: 'Learn More',
        watch_now: 'Watch Now',
        resume: 'Resume',
        completed: 'Completed',
        in_progress: 'In Progress',
        not_started: 'Not Started',
        time_spent: 'Time Spent',
        progress: 'Progress',
    },
    course: {
        modules: 'Modules', lessons: 'Lessons', quiz: 'Quiz', duration: 'Duration',
        instructor: 'Instructor', enroll: 'Enroll', enrolled: 'Enrolled',
        certificate_available: 'Certificate Available',
        points_on_completion: 'Points on Completion',
        play_video: 'Play Video', pause: 'Pause', mute: 'Mute', unmute: 'Unmute',
        seek_back: 'Skip back 10s', seek_forward: 'Skip forward 10s',
        non_skippable_notice: 'Please watch the full video before continuing',
    },
    quiz: {
        question: 'Question', of: 'of', time_left: 'Time left',
        read_question: 'Read question aloud', stop_reading: 'Stop reading',
        select_answer: 'Select an answer', submit_answer: 'Submit Answer',
        correct: 'Correct!', wrong: 'Wrong', explanation: 'Explanation',
        your_score: 'Your Score', passed: 'Passed', failed: 'Try Again',
        restart_prompt: 'You answered 3 in a row wrong. Restart the quiz for a fresh start?',
        restart_yes: 'Yes, restart', restart_no: 'No, continue',
    },
    rating: {
        rate_this_video: 'Rate this video', thanks_for_rating: 'Thanks for rating!',
        likes: 'likes', views: 'views', star: 'star', stars: 'stars',
    },
    cert: {
        download_certificate: 'Download Certificate', share: 'Share',
        issued_on: 'Issued on', valid_until: 'Valid until', skills_mastered: 'Skills mastered',
    },
    search: {
        placeholder: 'Search videos, courses, tags…',
        voice_search: 'Voice search',
        listening: 'Listening…',
        no_results: 'No results found',
        try_voice: 'Try voice search',
        recent: 'Recent',
        trending: 'Trending',
    },
    stories: { tap_to_advance: 'Tap to advance' },
    notif: {
        new_course: 'New course available',
        course_complete: 'Course completed!',
        badge_earned: 'Badge earned',
        points_earned: 'Points credited',
    },
};

// ---- Hindi (हिन्दी) — manually translated key strings ----
const HI = {
    common: {
        start: 'शुरू करें', continue: 'जारी रखें', save: 'सहेजें', cancel: 'रद्द करें', close: 'बंद',
        next: 'आगे', back: 'पीछे', loading: 'लोड हो रहा है…', error: 'कुछ गलत हो गया',
        retry: 'पुनः प्रयास', search: 'खोजें', filter: 'फ़िल्टर', clear: 'साफ़', submit: 'जमा करें',
        ok: 'ठीक है', yes: 'हाँ', no: 'नहीं', confirm: 'पुष्टि', delete: 'हटाएँ', edit: 'संपादित',
        login: 'लॉगिन', logout: 'साइन आउट', register: 'पंजीकरण', profile: 'प्रोफ़ाइल',
        select_language: 'भाषा चुनें', language: 'भाषा',
    },
    nav: {
        home: 'होम', courses: 'पाठ्यक्रम', arcade: 'आर्केड', leaders: 'लीडर्स',
        profile: 'प्रोफ़ाइल', certificates: 'प्रमाणपत्र', rewards: 'पुरस्कार',
    },
    trainee: {
        welcome: 'स्वागत है',
        my_courses: 'मेरे पाठ्यक्रम',
        all_courses: 'सभी पाठ्यक्रम',
        my_points: 'मेरे अंक',
        my_level: 'मेरा स्तर',
        my_badges: 'मेरे बैज',
        streak: 'स्ट्रीक',
        days: 'दिन',
        play_arcade: 'आर्केड खेलें',
        learn_more: 'और जानें',
        watch_now: 'अभी देखें',
        resume: 'जारी रखें',
        completed: 'पूर्ण',
        in_progress: 'चल रहा है',
        not_started: 'शुरू नहीं',
        time_spent: 'बिताया समय',
        progress: 'प्रगति',
    },
    course: {
        modules: 'मॉड्यूल', lessons: 'पाठ', quiz: 'क्विज़', duration: 'अवधि',
        instructor: 'प्रशिक्षक', enroll: 'नामांकन', enrolled: 'नामांकित',
        certificate_available: 'प्रमाणपत्र उपलब्ध',
        points_on_completion: 'पूरा करने पर अंक',
        play_video: 'वीडियो चलाएँ', pause: 'रोकें', mute: 'मूक', unmute: 'अनम्यूट',
        seek_back: '10 सेकंड पीछे', seek_forward: '10 सेकंड आगे',
        non_skippable_notice: 'जारी रखने से पहले पूरा वीडियो देखें',
    },
    quiz: {
        question: 'प्रश्न', of: 'में से', time_left: 'समय शेष',
        read_question: 'प्रश्न पढ़ें', stop_reading: 'पढ़ना रोकें',
        select_answer: 'एक उत्तर चुनें', submit_answer: 'उत्तर जमा करें',
        correct: 'सही!', wrong: 'गलत', explanation: 'व्याख्या',
        your_score: 'आपका स्कोर', passed: 'पास', failed: 'पुनः प्रयास करें',
        restart_prompt: 'आपने लगातार 3 गलत उत्तर दिए हैं। नई शुरुआत के लिए क्विज़ पुनः प्रारंभ करें?',
        restart_yes: 'हाँ, पुनः शुरू', restart_no: 'नहीं, जारी रखें',
    },
    rating: {
        rate_this_video: 'इस वीडियो को रेट करें', thanks_for_rating: 'रेटिंग के लिए धन्यवाद!',
        likes: 'पसंद', views: 'व्यू', star: 'सितारा', stars: 'सितारे',
    },
    cert: {
        download_certificate: 'प्रमाणपत्र डाउनलोड करें', share: 'साझा करें',
        issued_on: 'जारी तिथि', valid_until: 'मान्य तक', skills_mastered: 'कौशल हासिल',
    },
    search: {
        placeholder: 'वीडियो, पाठ्यक्रम, टैग खोजें…',
        voice_search: 'वॉइस सर्च',
        listening: 'सुन रहा हूँ…',
        no_results: 'कोई परिणाम नहीं मिला',
        try_voice: 'वॉइस सर्च आज़माएँ',
        recent: 'हाल का',
        trending: 'ट्रेंडिंग',
    },
    stories: { tap_to_advance: 'आगे बढ़ने के लिए टैप करें' },
    notif: {
        new_course: 'नया पाठ्यक्रम उपलब्ध',
        course_complete: 'पाठ्यक्रम पूरा!',
        badge_earned: 'बैज अर्जित',
        points_earned: 'अंक जमा',
    },
};

// Stub locales — copy English so UI is never empty. Translation team can fill these in
// by editing this file. Falling back at the resolver level also means missing keys never
// render as empty/undefined strings.
const STUB = (label) => ({ __stub_for: label, ...EN });

const DICTS = {
    en: EN,
    hi: HI,
    ta: STUB('ta'),
    ml: STUB('ml'),
    te: STUB('te'),
    kn: STUB('kn'),
    bn: STUB('bn'),
    or: STUB('or'),
    mr: STUB('mr'),
    gu: STUB('gu'),
};

// ---- Resolver: dotted-path with fallback to English ----
function resolve(dict, path) {
    const parts = path.split('.');
    let cur = dict;
    for (const p of parts) {
        if (cur == null) return null;
        cur = cur[p];
    }
    return typeof cur === 'string' ? cur : null;
}

// ---- Context ----
const I18nContext = createContext({ locale: 'en', t: (k) => k, setLocale: () => {} });

const LS_KEY = 'lms.locale';

export function I18nProvider({ children, initial }) {
    const [locale, setLocaleState] = useState(() => {
        if (initial && DICTS[initial]) return initial;
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(LS_KEY);
            if (saved && DICTS[saved]) return saved;
        }
        return 'en';
    });

    const setLocale = useCallback((next) => {
        if (!DICTS[next]) return;
        setLocaleState(next);
        if (typeof window !== 'undefined') localStorage.setItem(LS_KEY, next);
        // Tag <html lang="…"> so screen readers + Web Speech pick the right voice.
        if (typeof document !== 'undefined') document.documentElement.lang = next;
    }, []);

    useEffect(() => {
        if (typeof document !== 'undefined') document.documentElement.lang = locale;
    }, [locale]);

    const t = useCallback((key, fallback) => {
        const dict = DICTS[locale] || DICTS.en;
        const value = resolve(dict, key) || resolve(DICTS.en, key);
        return value ?? fallback ?? key;
    }, [locale]);

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }
export function useT() { return useContext(I18nContext).t; }
export function useLocale() {
    const { locale, setLocale } = useContext(I18nContext);
    return [locale, setLocale];
}

// Simple language switcher — drop into navbar / profile.
import { Globe, Check } from 'lucide-react';

export function LanguageSwitcher({ compact = false, className = '' }) {
    const [open, setOpen] = useState(false);
    const [locale, setLocale] = useLocale();
    const current = LOCALES.find((l) => l.code === locale) || LOCALES[0];
    return (
        <div className={`relative inline-block ${className}`} data-testid="lang-switcher">
            <button
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold ring-1 ring-slate-200 transition"
                data-testid="lang-switcher-btn"
                title="Change language"
            >
                <Globe className="w-3.5 h-3.5" />
                {compact ? current.code.toUpperCase() : current.native}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 z-40 mt-1.5 w-44 max-h-72 overflow-y-auto rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 py-1"
                        data-testid="lang-switcher-menu">
                        {LOCALES.map((l) => (
                            <button
                                key={l.code}
                                onClick={() => { setLocale(l.code); setOpen(false); }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-amber-50 transition ${
                                    l.code === locale ? 'bg-amber-100/60 font-bold text-amber-900' : 'text-slate-700'
                                }`}
                                data-testid={`lang-opt-${l.code}`}
                            >
                                <span>{l.native} <span className="text-[10px] opacity-60">({l.label})</span></span>
                                {l.code === locale && <Check className="w-4 h-4 text-amber-700" />}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
